/*
  GAROE Moments, race plaques live preview (several races, one product).

  race-plaque-preview  Draws the runner's details onto the chosen race's plaque
                       as the customer types. The race decides:
                         - the artwork, which already carries the finish (silver
                           or gold) and every fixed label
                         - the layout: where each detail sits, in the artwork's
                           own pixel space (see LAYOUTS below)
                         - the title line, the distance used for average pace
                         - which split fields are shown, and which are submitted

  Race data comes from the section, in a JSON script tag with data-race-data.
  Layouts live here, keyed by the race key, because they are measured from the
  artwork: a new race means a new artwork file, a Race block, and one entry in
  LAYOUTS. A race without an entry uses the Cardiff Half layout.

  Each detail shrinks to fit its space so nothing runs off the plaque. A long
  name drops onto two lines.

  Worked out, never typed:
    - the last split is the finish time
    - the average pace is the finish time divided by the race distance in
      miles, shown as MM:SS
    - the year in the title line comes from the race date

  Times are read as h:mm:ss, or mm:ss when there are only two parts. The splits
  are checked to be in order, and on submit every time is normalised so the
  order carries the same format that is printed. The inputs for races that are
  not selected are disabled, so they are neither validated nor submitted.
*/
(() => {
  if (customElements.get('race-plaque-preview')) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const config = window.GAROE || { strings: {} };
  const strings = config.strings || {};
  const TIME = /^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/;
  const CELL_MAX = 10;

  const FONTS = {
    poppins: ["'GAROE Plaque', system-ui, sans-serif", 400],
    poppinsBold: ["'GAROE Plaque', system-ui, sans-serif", 700],
    numbers: ["'GAROE Plaque Numbers', system-ui, sans-serif", 700],
    playfair: ["'GAROE Playfair', Georgia, serif", 700],
    roboto: ["'GAROE Roboto', system-ui, sans-serif", 400],
    robotoCondensed: ["'GAROE Roboto Condensed', system-ui, sans-serif", 700],
  };

  const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

  /*
    Layouts. Numbers are pixels on the artwork. x is the centre of the text, or
    its left edge when anchor is 'start'. y is the baseline. size is the
    starting size, min the smallest it may shrink to, space the letter spacing
    in em, max the widest it may be. Positions below were measured from the
    designs with a sample runner, then checked against them.
  */
  const LAYOUTS = {
    'cardiff-half': {
      w: 1260,
      h: 1782,
      hours: { finish: 1, split: 2 },
      name: { mode: 'center', font: 'poppinsBold', x: 628, y: 1200, size: 92, min: 52, space: 0.255, max: 1130, twoSize: 50, twoGap: 58 },
      subtitle: { font: 'poppins', x: 628, y: 1262, size: 36, min: 22, space: 0.3, max: 1130 },
      finish: { font: 'numbers', x: 507, y: 1374, size: 49, min: 30, space: 0, max: 128 },
      pace: { font: 'numbers', x: 836, y: 1368, size: 49, min: 30, space: 0, max: 118 },
      bib: { font: 'numbers', x: 1145, y: 1376, size: 49, min: 30, space: 0, max: 118 },
      cells: { font: 'poppins', y: 1643, size: 27, min: 14, space: 0.096, max: 135, xs: [182, 393, 609, 828, 1054], start: 150, end: 1110 },
    },

    'london-marathon': {
      w: 2000,
      h: 1414,
      hours: { finish: 2, split: 2 },
      name: { mode: 'center', font: 'poppinsBold', x: 1000, y: 688, size: 121, min: 50, space: 0.222, max: 1500 },
      subtitle: { font: 'poppins', x: 1000, y: 742, size: 38, min: 24, space: 0.28, max: 1500 },
      date: { font: 'poppins', x: 1000, y: 794, size: 29, min: 20, space: 0.202, max: 900, order: 'mdy', sup: true },
      finish: { font: 'numbers', x: 1000, y: 939, size: 100, min: 60, space: -0.015, max: 420 },
      pace: { font: 'numbers', x: 1011, y: 1087, size: 60, min: 36, space: 0, max: 150 },
      bib: { font: 'numbers', x: 1345, y: 1098, size: 60, min: 36, space: 0, max: 190 },
      cells: { font: 'poppins', y: 1343, size: 30, min: 18, space: 0.039, max: 140, xs: [264, 430, 593, 747, 906, 1073, 1241, 1406, 1567, 1732], start: 264, end: 1732 },
    },

    'swansea-bay-10k': {
      w: 2000,
      h: 1414,
      hours: { finish: 2, split: 2 },
      name: { mode: 'stack', font: 'playfair', x: 58, y: 475, pitch: 107, size: 110, min: 40, space: 0, max: 575, anchor: 'start' },
      date: { font: 'roboto', x: 60, y: 607, size: 26, min: 16, space: 0.478, max: 560, order: 'dmy', anchor: 'start' },
      finish: { font: 'robotoCondensed', x: 61, y: 889, size: 57, min: 34, space: 0.014, max: 300, anchor: 'start' },
      pace: { font: 'robotoCondensed', x: 62, y: 1029, size: 57, min: 34, space: 0, max: 300, anchor: 'start' },
      bib: { font: 'robotoCondensed', x: 62, y: 1186, size: 57, min: 34, space: 0, max: 300, anchor: 'start' },
    },
  };

  const pad = (n) => String(n).padStart(2, '0');

  /* "1:31:49" or "21:45" to seconds, or null if it is not a time. */
  /* Time fields add their own colons: type digits only, filled from the right like a stopwatch. */
  function formatTime(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 2) return digits;
    const secs = digits.slice(-2);
    const mins = digits.slice(-4, -2);
    const hours = digits.slice(0, -4);
    return hours ? `${hours}:${mins}:${secs}` : `${mins}:${secs}`;
  }

  function parseTime(value) {
    const match = TIME.exec((value || '').trim());
    if (!match) return null;
    if (match[3] === undefined) return Number(match[1]) * 60 + Number(match[2]);
    return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]);
  }

  function clock(seconds, hourDigits) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${hourDigits === 2 ? pad(h) : h}:${pad(m)}:${pad(s)}`;
  }

  function paceText(seconds, miles) {
    if (!seconds || !miles) return '';
    const perMile = Math.round(seconds / miles);
    return `${pad(Math.floor(perMile / 60))}:${pad(perMile % 60)}`;
  }

  /* 1ST, 2ND, 3RD, 4TH ... 11TH, 12TH, 13TH ... 21ST */
  function ordinal(day) {
    const rest = day % 100;
    if (rest >= 11 && rest <= 13) return 'TH';
    return { 1: 'ST', 2: 'ND', 3: 'RD' }[day % 10] || 'TH';
  }

  /* "2026-09-20" to its parts, or null. */
  function parseDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { year, month: MONTHS[month - 1], day, suffix: ordinal(day) };
  }

  const clean = (list) => (Array.isArray(list) ? list.map((item) => String(item).trim()).filter(Boolean) : []);

  class RacePlaquePreview extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      this.form = this.section ? this.section.querySelector('form') : null;
      this.paceProperty = this.dataset.paceProperty || 'Average pace (min/mile)';
      this.finishLabels = {
        silver: this.dataset.silverLabel || 'Brushed silver',
        gold: this.dataset.goldLabel || 'Brushed gold',
      };
      this.desc = this.querySelector('[data-plaque-desc]');
      this.svg = this.querySelector('[data-plaque-svg]');
      this.metal = this.querySelector('[data-plaque-metal]');
      this.art = this.querySelector('[data-plaque-art]');
      this.artNote = this.querySelector('[data-plaque-art-note]');
      this.artLabel = this.querySelector('[data-plaque-art-label]');
      this.layer = this.querySelector('[data-plaque-layer]');
      this.paceNote = this.section ? this.section.querySelector('[data-plaque-pace-note]') : null;
      this.finishNote = this.section ? this.section.querySelector('[data-finish-note]') : null;
      this.finishInput = this.section ? this.section.querySelector('[data-finish-input]') : null;
      this.splitsGroup = this.section ? this.section.querySelector('[data-splits-group]') : null;

      this.races = this.readRaces();
      if (!this.races.length || !this.layer) return;
      this.race = this.races[0];
      this.nodes = {};

      this.inputs = {};
      if (this.form) {
        this.form.querySelectorAll('[data-plaque-input]:not([data-plaque-input="split"])').forEach((node) => {
          this.inputs[node.dataset.plaqueInput] = node;
        });
      }

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-board-tab]');
        if (tab) this.show(tab.dataset.boardTab);
      });

      if (this.form) {
        this.form.addEventListener('input', (event) => {
          const timeField = event.target.closest('.field__input--time');
          if (timeField) {
            const formatted = formatTime(timeField.value);
            if (formatted !== timeField.value) timeField.value = formatted;
          }
          const field = event.target.closest('[data-plaque-input]');
          if (field) field.setCustomValidity('');
          this.render(Boolean(field));
        });
        this.form.addEventListener('change', (event) => {
          const radio = event.target.closest('[data-race-radio]');
          if (radio && radio.dataset.raceRadio === '__request') {
            this.setRequestMode(true);
          } else if (radio) {
            this.setRequestMode(false);
            this.selectRace(radio.dataset.raceRadio, true);
          }
          if (event.target.closest('[data-plaque-input]')) this.render(true);
        });
      }

      // The "earn your finish" cards also pick the race.
      if (this.section) {
        this.section.querySelectorAll('[data-select-race]').forEach((button) => {
          button.addEventListener('click', () => this.chooseFromCard(button.dataset.selectRace));
        });
      }

      // The product form fires its events on the custom element around the form.
      const productForm = this.form ? this.form.closest('product-form') : null;
      if (productForm) {
        productForm.addEventListener('product-form:validate', () => this.validate());
        productForm.addEventListener('product-form:serialize', (event) => this.serialize(event.detail.properties));
      }

      // After sending a request the page reloads, so open the request panel again.
      const requestPanel = this.section ? this.section.querySelector('[data-race-request]') : null;
      if (requestPanel && requestPanel.querySelector('[data-request-sent], [data-request-error]') && this.form) {
        const requestRadio = this.form.querySelector('[data-race-radio="__request"]');
        if (requestRadio) requestRadio.checked = true;
      }

      const checked = this.form ? this.form.querySelector('[data-race-radio]:checked') : null;
      const startInRequest = checked && checked.dataset.raceRadio === '__request';
      this.selectRace(checked && !startInRequest ? checked.dataset.raceRadio : this.race.key, false);
      if (startInRequest) this.setRequestMode(true);
      // Fitting depends on the plaque fonts, so fit again once they have loaded.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.render(false));
    }

    readRaces() {
      const script = this.section ? this.section.querySelector('script[data-race-data]') : null;
      if (!script) return [];
      try {
        return JSON.parse(script.textContent).map((race) => ({
          key: race.key,
          name: race.name,
          finish: race.finish === 'gold' ? 'gold' : 'silver',
          miles: parseFloat(race.miles) || 0,
          subtitle: race.subtitle || '',
          ink: race.ink === 'light' ? 'light' : 'dark',
          art: race.art || '',
          splits: clean(race.splits),
          splitX: clean(race.splitX).map(Number).filter((n) => Number.isFinite(n)),
        }));
      } catch (error) {
        return [];
      }
    }

    /* "Another race" swaps the plaque form for a request form. */
    setRequestMode(on) {
      if (!this.section) return;
      const panel = this.section.querySelector('[data-race-request]');
      if (panel && !panel.dataset.timeMask) {
        panel.dataset.timeMask = '1';
        panel.addEventListener('input', (event) => {
          const field = event.target.closest('.field__input--time');
          if (field) field.value = formatTime(field.value);
        });
      }
      if (on) this.section.setAttribute('data-request-mode', '');
      else this.section.removeAttribute('data-request-mode');
      if (panel) panel.hidden = !on;
      if (on) {
        if (this.finishNote) this.finishNote.textContent = '';
        if (this.querySelector('[data-board-panel="photos"]')) this.show('photos');
      }
    }

    chooseFromCard(key) {
      const radio = this.form ? this.form.querySelector(`[data-race-radio="${key}"]`) : null;
      if (radio) radio.checked = true;
      this.selectRace(key, true);
    }

    layout() {
      return LAYOUTS[this.race.key] || LAYOUTS['cardiff-half'];
    }

    /* Everything that depends on the race. */
    selectRace(key, fromCard) {
      const race = this.races.find((item) => item.key === key) || this.races[0];
      this.race = race;
      // Each race is its own variant, so the cart line and the Google feed know the race.
      const raceRadio = this.form ? this.form.querySelector(`[data-race-radio="${race.key}"]`) : null;
      const variantInput = this.form ? this.form.querySelector('[data-variant-input]') : null;
      if (raceRadio && variantInput && raceRadio.dataset.variantId) {
        variantInput.value = raceRadio.dataset.variantId;
        // Keep the address in step so a shared link opens on the same race.
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.get('variant') !== raceRadio.dataset.variantId) {
            url.searchParams.set('variant', raceRadio.dataset.variantId);
            window.history.replaceState(null, '', url.toString());
          }
        } catch (error) { /* ignore */ }
      }
      const layout = this.layout();
      const finishName = this.finishLabels[race.finish];

      if (this.svg) {
        this.svg.setAttribute('viewBox', `0 0 ${layout.w} ${layout.h}`);
        this.svg.style.aspectRatio = `${layout.w} / ${layout.h}`;
        const light = race.ink === 'light';
        this.svg.style.setProperty('--plaque-ink', light ? '#f3f3ef' : '#1a1a1a');
        this.svg.style.setProperty('--plaque-ink-faint', light ? 'rgba(243, 243, 239, 0.38)' : 'rgba(26, 26, 26, 0.32)');
      }
      [this.metal, this.art].forEach((node) => {
        if (!node) return;
        node.setAttribute('width', layout.w);
        node.setAttribute('height', layout.h);
      });
      if (this.metal) {
        this.metal.setAttribute('fill', this.metal.getAttribute('fill').replace(/(Silver|Gold)-/, race.finish === 'gold' ? 'Gold-' : 'Silver-'));
      }
      if (this.art) {
        if (race.art) {
          this.art.setAttribute('href', race.art);
          this.art.removeAttribute('display');
          this.art.removeAttribute('hidden');
        } else {
          this.art.setAttribute('display', 'none');
        }
      }
      if (this.artNote) this.artNote.setAttribute('display', race.art ? 'none' : 'inline');
      if (this.artLabel) {
        this.artLabel.textContent = race.name.toUpperCase();
        this.artLabel.setAttribute('x', layout.w / 2);
      }

      if (this.finishInput) this.finishInput.value = finishName;
      if (this.finishNote) this.finishNote.textContent = `${race.name}: ${finishName}`;

      // Show this race's split fields, and only submit those.
      if (this.form) {
        this.form.querySelectorAll('[data-race-splits]').forEach((panel) => {
          const active = panel.dataset.raceSplits === race.key;
          panel.hidden = !active;
          panel.querySelectorAll('input').forEach((input) => {
            input.disabled = !active;
            input.required = active;
            if (!active) input.setCustomValidity('');
          });
        });
      }
      if (this.splitsGroup) this.splitsGroup.hidden = race.splits.length === 0;

      if (this.section) {
        this.section.querySelectorAll('[data-select-race]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.selectRace === race.key));
        });
      }

      // New layout, new text nodes.
      this.layer.textContent = '';
      this.nodes = {};

      this.render(false);
      if (fromCard && this.querySelector('[data-board-panel="preview"]') && this.dataset.view !== 'preview') {
        this.show('preview');
      }
    }

    show(view) {
      this.dataset.view = view;
      this.querySelectorAll('[data-board-tab]').forEach((tab) => {
        tab.setAttribute('aria-pressed', String(tab.dataset.boardTab === view));
      });
      this.querySelectorAll('[data-board-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.boardPanel !== view;
      });
      // Text can only be measured once the preview is visible, so fit it again.
      if (view === 'preview') this.render(false);
    }

    value(key) {
      return this.inputs[key] ? this.inputs[key].value.trim() : '';
    }

    time(key) {
      return parseTime(this.value(key));
    }

    /* The enabled split inputs for the chosen race, in order. */
    splitInputs() {
      if (!this.form) return [];
      return [...this.form.querySelectorAll(`[data-race-splits="${this.race.key}"] input[data-plaque-input="split"]`)];
    }

    render(fromTyping) {
      const race = this.race;
      const layout = this.layout();
      const name = this.value('name').toUpperCase();
      const bib = this.value('bib').toUpperCase();
      const date = parseDate(this.value('date'));
      const finish = this.time('finish');
      const pace = finish ? paceText(finish, race.miles) : '';
      const finishHours = layout.hours.finish;
      const splitHours = layout.hours.split;
      const zero = finishHours === 2 ? '00:00:00' : '0:00:00';

      this.setName(layout.name, name || strings.plaqueNamePlaceholder || 'YOUR NAME', !name);
      if (layout.subtitle) this.setSubtitle(layout.subtitle, date);
      if (layout.date) this.setDate(layout.date, date);
      this.setText('finish', layout.finish, finish ? clock(finish, finishHours) : zero, !finish);
      this.setText('pace', layout.pace, pace || '00:00', !pace);
      this.setText('bib', layout.bib, bib || '0000', !bib);

      // Split cells: each split the race has, then the finish time.
      const inputs = layout.cells ? this.splitInputs() : [];
      const count = inputs.length > 0 ? Math.min(inputs.length, CELL_MAX - 1) + 1 : 0;
      const xs = this.cellPositions(layout, count);
      const step = count > 1 ? Math.abs(xs[count - 1] - xs[0]) / (count - 1) : layout.cells ? layout.cells.max : 0;
      for (let i = 0; i < CELL_MAX; i += 1) {
        if (i >= count) {
          if (this.nodes[`c${i}`]) this.nodes[`c${i}`].textContent = '';
          continue;
        }
        const last = i === count - 1;
        const seconds = last ? finish : parseTime(inputs[i].value);
        const cfg = { ...layout.cells, x: xs[i], max: Math.min(layout.cells.max, step * 0.86) };
        this.setText(`c${i}`, cfg, seconds ? clock(seconds, splitHours) : '00:00:00', !seconds);
      }

      if (this.paceNote) {
        if (pace) {
          this.paceNote.textContent = (strings.plaquePaceNote || 'Average pace: {pace} per mile, worked out from your finish time.').replace('{pace}', pace);
          this.paceNote.hidden = false;
        } else {
          this.paceNote.hidden = true;
        }
      }

      if (this.desc) {
        const bits = [name, race.name].filter(Boolean).join(', ');
        this.desc.textContent = `${this.dataset.alt || ''} ${bits}`.trim();
      }

      // Typing brings the preview forward if the photos are showing.
      if (fromTyping && this.dataset.view !== 'preview' && this.querySelector('[data-board-panel="preview"]')) {
        this.show('preview');
      }
    }

    /* A split's x positions: the Race block's own, then the layout's, then even. */
    cellPositions(layout, count) {
      if (!count || !layout.cells) return [];
      if (this.race.splitX.length === count) return this.race.splitX;
      if (layout.cells.xs && layout.cells.xs.length === count) return layout.cells.xs;
      if (count === 1) return [(layout.cells.start + layout.cells.end) / 2];
      const step = (layout.cells.end - layout.cells.start) / (count - 1);
      return Array.from({ length: count }, (_, i) => Math.round(layout.cells.start + step * i));
    }

    /* The text node for a detail, made once per layout. */
    node(key, cfg) {
      if (!this.nodes[key]) {
        const el = document.createElementNS(SVG_NS, 'text');
        el.setAttribute('class', 'plaque-text');
        this.layer.appendChild(el);
        this.nodes[key] = el;
      }
      const el = this.nodes[key];
      const [family, weight] = FONTS[cfg.font] || FONTS.poppins;
      el.style.fontFamily = family;
      el.style.fontWeight = weight;
      el.style.textAnchor = cfg.anchor === 'start' ? 'start' : 'middle';
      return el;
    }

    /* Prepare a node for new text. */
    prepare(key, cfg, placeholder) {
      const el = this.node(key, cfg);
      el.classList.toggle('is-placeholder', placeholder);
      el.textContent = '';
      el.removeAttribute('x');
      el.setAttribute('y', cfg.y);
      return el;
    }

    /* Set size and spacing, and the x that centres on (or starts at) the point. */
    place(el, cfg, size) {
      const spacing = (cfg.space || 0) * size;
      el.setAttribute('font-size', size.toFixed(1));
      el.setAttribute('letter-spacing', spacing.toFixed(2));
      // The trailing letter-spacing shifts centred text, so allow for it.
      el.setAttribute('x', (cfg.anchor === 'start' ? cfg.x : cfg.x + spacing / 2).toFixed(1));
    }

    /* Shrink a prepared node until it fits. */
    shrink(el, cfg) {
      this.place(el, cfg, cfg.size);
      const width = this.measure(el);
      if (width > cfg.max) {
        this.place(el, cfg, Math.max(cfg.min, (cfg.size * cfg.max) / width));
      }
    }

    /* Single line that shrinks to fit. */
    setText(key, cfg, text, placeholder) {
      if (!cfg) return;
      const el = this.prepare(key, cfg, placeholder);
      el.textContent = text;
      this.shrink(el, cfg);
    }

    /* "2025 CARDIFF HALF MARATHON FINISHER", or any title using {year}. */
    setSubtitle(cfg, date) {
      const el = this.prepare('subtitle', cfg, false);
      const template = this.race.subtitle || '';
      const parts = template.includes('{year}') ? template.split('{year}') : ['', ` ${template}`];
      const yearNode = document.createElementNS(SVG_NS, 'tspan');
      yearNode.textContent = date ? String(date.year) : strings.plaqueYearPlaceholder || 'YEAR';
      if (!date) yearNode.setAttribute('class', 'is-placeholder');
      el.appendChild(document.createTextNode(parts[0]));
      el.appendChild(yearNode);
      el.appendChild(document.createTextNode(parts.slice(1).join('{year}')));
      this.shrink(el, cfg);
    }

    /* "APRIL 27TH 2025" or "20TH SEPTEMBER 2026", from the date field. */
    setDate(cfg, date) {
      const el = this.prepare('date', cfg, !date);
      if (!date) {
        el.textContent = strings.plaqueDatePlaceholder || 'RACE DATE';
        this.shrink(el, cfg);
        return;
      }
      const ordinalNode = () => {
        if (!cfg.sup) return document.createTextNode(`${date.day}${date.suffix}`);
        const frag = document.createDocumentFragment();
        frag.appendChild(document.createTextNode(String(date.day)));
        const sup = document.createElementNS(SVG_NS, 'tspan');
        sup.setAttribute('baseline-shift', 'super');
        sup.setAttribute('font-size', '65%');
        sup.textContent = date.suffix;
        frag.appendChild(sup);
        return frag;
      };
      if (cfg.order === 'mdy') {
        el.appendChild(document.createTextNode(`${date.month} `));
        el.appendChild(ordinalNode());
        el.appendChild(document.createTextNode(` ${date.year}`));
      } else {
        el.appendChild(ordinalNode());
        el.appendChild(document.createTextNode(` ${date.month} ${date.year}`));
      }
      this.shrink(el, cfg);
    }

    /* Centred: one line if it fits at a readable size, otherwise two balanced
       lines. Stacked: left aligned lines, built up from the bottom line. */
    setName(cfg, text, placeholder) {
      if (!cfg) return;
      if (cfg.mode === 'stack') {
        this.setStackedName(cfg, text, placeholder);
        return;
      }
      const el = this.prepare('name', cfg, placeholder);
      el.textContent = text;
      this.place(el, cfg, cfg.size);
      const width = this.measure(el);
      if (width <= cfg.max) return;

      const oneLineSize = (cfg.size * cfg.max) / width;
      const words = text.split(/\s+/);
      if (oneLineSize >= cfg.min || words.length < 2 || !cfg.twoSize) {
        this.place(el, cfg, Math.max(oneLineSize, Math.min(cfg.min, 20)));
        return;
      }

      const [first, second] = this.balance(words);
      el.textContent = '';
      el.removeAttribute('x');
      const lines = [first, second].map((line) => {
        const tspan = document.createElementNS(SVG_NS, 'tspan');
        tspan.textContent = line;
        el.appendChild(tspan);
        return tspan;
      });
      el.setAttribute('font-size', cfg.twoSize);
      el.setAttribute('letter-spacing', (cfg.space * cfg.twoSize).toFixed(2));
      const widest = Math.max(...lines.map((tspan) => this.measure(tspan)));
      const size = widest > cfg.max ? (cfg.twoSize * cfg.max) / widest : cfg.twoSize;
      el.setAttribute('font-size', size.toFixed(1));
      el.setAttribute('letter-spacing', (cfg.space * size).toFixed(2));
      const x = (cfg.x + (cfg.space * size) / 2).toFixed(1);
      // Second line sits just below the one-line baseline, the first above it.
      const secondY = cfg.y + 8;
      lines[0].setAttribute('x', x);
      lines[0].setAttribute('y', (secondY - cfg.twoGap).toFixed(1));
      lines[1].setAttribute('x', x);
      lines[1].setAttribute('y', secondY.toFixed(1));
    }

    /* One or two left aligned lines ending on the baseline at cfg.y. */
    setStackedName(cfg, text, placeholder) {
      const el = this.prepare('name', cfg, placeholder);
      const words = text.split(/\s+/).filter(Boolean);
      const lines = words.length > 1 ? this.balance(words) : [words[0] || ''];
      let size = cfg.size;
      el.setAttribute('font-size', size);
      el.setAttribute('letter-spacing', ((cfg.space || 0) * size).toFixed(2));
      const tspans = lines.map((line) => {
        const tspan = document.createElementNS(SVG_NS, 'tspan');
        tspan.textContent = line;
        el.appendChild(tspan);
        return tspan;
      });
      const widest = Math.max(...tspans.map((tspan) => this.measure(tspan)));
      if (widest > cfg.max) {
        // Always fit: a long name is better small than running off the plaque.
        size = Math.max(Math.min(cfg.min, 24), (cfg.size * cfg.max) / widest);
        el.setAttribute('font-size', size.toFixed(1));
        el.setAttribute('letter-spacing', ((cfg.space || 0) * size).toFixed(2));
      }
      const pitch = cfg.pitch * (size / cfg.size);
      tspans.forEach((tspan, index) => {
        tspan.setAttribute('x', cfg.x);
        tspan.setAttribute('y', (cfg.y - pitch * (tspans.length - 1 - index)).toFixed(1));
      });
    }

    measure(node) {
      try {
        return node.getComputedTextLength();
      } catch (error) {
        return 0;
      }
    }

    /* Split at the space that makes the two lines closest in length. */
    balance(words) {
      let best = 1;
      let bestDiff = Infinity;
      for (let i = 1; i < words.length; i += 1) {
        const diff = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = i;
        }
      }
      return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
    }

    /* The times must run in order: each split, then the finish. */
    validate() {
      const splits = this.splitInputs();
      const fields = [...splits, this.inputs.finish].filter(Boolean);
      fields.forEach((field) => field.setCustomValidity(''));
      let previous = 0;
      for (const field of fields) {
        const seconds = parseTime(field.value);
        if (seconds === null) return;
        if (seconds <= previous) {
          const isFinish = field === this.inputs.finish;
          const last = splits.length ? splits[splits.length - 1].closest('.field').querySelector('label').textContent.trim() : '';
          const message = isFinish && splits.length
            ? `Your finish time needs to be after your ${last} split.`
            : 'Each split needs to be later than the one before it.';
          field.setCustomValidity(message);
          return;
        }
        previous = seconds;
      }
    }

    /* Send the times in one tidy format, and add the worked-out pace. The race
       date is formatted by the product form. */
    serialize(properties) {
      if (!properties) return;
      const layout = this.layout();
      const nameOf = (input) => {
        const match = input && /^properties\[(.+)\]$/.exec(input.name);
        return match ? match[1] : null;
      };
      const set = (input, value) => {
        const name = nameOf(input);
        if (name && value) properties[name] = value;
      };

      set(this.inputs.name, this.value('name').toUpperCase());
      set(this.inputs.bib, this.value('bib'));
      const finish = this.time('finish');
      if (finish) set(this.inputs.finish, clock(finish, layout.hours.finish));
      this.splitInputs().forEach((input) => {
        const seconds = parseTime(input.value);
        if (seconds) set(input, clock(seconds, layout.hours.split));
      });
      const pace = finish ? paceText(finish, this.race.miles) : '';
      if (pace) properties[this.paceProperty] = pace;
    }
  }

  customElements.define('race-plaque-preview', RacePlaquePreview);
})();
